// File: frontend/src/pages/SuperAdmin/components/Dashboard/index.js

import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBuilding, faCheckCircle, faUsers, faUserClock, 
  faCreditCard, faExclamationTriangle, faEnvelope, faBox,
  faUserCheck, faFileContract, faSync, faBell, faCalendarAlt,
  faPlusCircle, faUserPlus, faBoxOpen, faFileAlt, faExclamationCircle,
  faTachometerAlt, faKey, faShieldAlt, faDatabase, faChartBar, faHistory, 
  faCog, faQuestionCircle, faArrowRight, faChalkboardTeacher, faProjectDiagram
} from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../../../../contexts/LanguageContext';
import SuperAdminCard from '../common/SuperAdminCard';
import DonutChart from '../common/DonutChart';
import BarChart from '../common/BarChart';
import styles from './Dashboard.module.css';

const Dashboard = ({ onNavigate }) => {
  const { t } = useLanguage();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Fetch dashboard data
  const fetchDashboardData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // This would be replaced with actual API calls
      // const response = await dashboardService.getStats();
      
      // Mock data for now
      await new Promise(resolve => setTimeout(resolve, 800));
      
      setDashboardData({
        report_date: new Date().toISOString(),
        enterprise_metrics: {
          total_enterprises: 42,
          active_enterprises: 38,
          enterprises_by_status: {
            active: { count: 38 },
            pending: { count: 2 },
            suspended: { count: 1 },
            inactive: { count: 1 }
          },
          enterprises_by_type: {
            corporate: { count: 25 },
            government: { count: 8 },
            educational: { count: 5 },
            nonprofit: { count: 4 }
          }
        },
        user_metrics: {
          total_users: 1250,
          active_users: 1180,
          pending_verifications: 15,
          users_by_role: {
            admin: { count: 85 },
            manager: { count: 210 },
            analyst: { count: 580 },
            user: { count: 375 }
          }
        },
        product_metrics: {
          total_products: 12,
          active_products: 12,
          products_by_category: {
            analytics: { count: 4 },
            risk_management: { count: 3 },
            compliance: { count: 3 },
            reporting: { count: 2 }
          }
        },
        subscription_metrics: {
          total_subscriptions: 40,
          active_subscriptions: 40,
          expiring_soon: 5,
          subscriptions_by_plan: {
            enterprise: { count: 15 },
            professional: { count: 20 },
            standard: { count: 5 }
          }
        },
        agreement_metrics: {
          total_agreements: 45,
          active_agreements: 42,
          expiring_agreements: 3,
          agreements_by_type: {
            service: { count: 25 },
            license: { count: 15 },
            partnership: { count: 5 }
          }
        },
        communication_metrics: {
          total_communications: 156,
          communications_by_type: {
            email: { count: 120 },
            notification: { count: 30 },
            announcement: { count: 6 }
          }
        },
        dataset_metrics: {
          total_datasets: 28,
          active_datasets: 25,
          datasets_by_type: {
            reference: { count: 12 },
            transactional: { count: 10 },
            analytical: { count: 6 }
          }
        },
        report_metrics: {
          total_reports: 35,
          reports_by_type: {
            system: { count: 15 },
            enterprise: { count: 12 },
            user: { count: 8 }
          }
        },
        audit_metrics: {
          total_logs: 1250,
          logs_by_severity: {
            info: { count: 950 },
            warning: { count: 250 },
            error: { count: 50 }
          }
        },
        rbac_metrics: {
          total_roles: 15,
          total_permissions: 120,
          custom_roles: 8
        },
        pitch_access_metrics: {
          total_presentations: 25,
          active_presentations: 20,
          presentations_by_status: {
            active: { count: 20 },
            draft: { count: 3 },
            archived: { count: 2 }
          }
        },
        settings_metrics: {
          system_settings: 35,
          custom_settings: 12
        },
        recentActivities: [
          {
            id: 1,
            type: 'enterprise_created',
            entity: 'Acme Corporation',
            user: 'John Admin',
            timestamp: '2023-07-15T10:30:00Z'
          },
          {
            id: 2,
            type: 'user_verified',
            entity: 'jane.doe@example.com',
            user: 'Sarah Admin',
            timestamp: '2023-07-15T09:45:00Z'
          },
          {
            id: 3,
            type: 'agreement_updated',
            entity: 'Global Industries',
            user: 'John Admin',
            timestamp: '2023-07-14T16:20:00Z'
          },
          {
            id: 4,
            type: 'product_added',
            entity: 'Premium Analytics',
            user: 'Mike Admin',
            timestamp: '2023-07-14T14:10:00Z'
          },
          {
            id: 5,
            type: 'subscription_renewed',
            entity: 'Tech Solutions Inc',
            user: 'Sarah Admin',
            timestamp: '2023-07-14T11:05:00Z'
          }
        ],
        recentEnterprises: [
          {
            id: 1,
            name: 'Acme Corporation',
            type: 'corporate',
            status: 'active',
            users: 45,
            subscription_plan: 'enterprise',
            created_at: '2023-07-15T10:30:00Z'
          },
          {
            id: 2,
            name: 'Global Industries',
            type: 'corporate',
            status: 'active',
            users: 32,
            subscription_plan: 'professional',
            created_at: '2023-07-10T14:20:00Z'
          },
          {
            id: 3,
            name: 'Tech Solutions Inc',
            type: 'corporate',
            status: 'active',
            users: 28,
            subscription_plan: 'enterprise',
            created_at: '2023-07-05T09:15:00Z'
          },
          {
            id: 4,
            name: 'City of Metropolis',
            type: 'government',
            status: 'active',
            users: 65,
            subscription_plan: 'enterprise',
            created_at: '2023-07-01T11:45:00Z'
          },
          {
            id: 5,
            name: 'State University',
            type: 'educational',
            status: 'active',
            users: 120,
            subscription_plan: 'professional',
            created_at: '2023-06-28T08:30:00Z'
          }
        ],
        recentUsers: [
          {
            id: 1,
            email: 'john.smith@acmecorp.com',
            name: 'John Smith',
            enterprise: 'Acme Corporation',
            role: 'admin',
            status: 'active',
            created_at: '2023-07-15T11:30:00Z'
          },
          {
            id: 2,
            email: 'jane.doe@globalind.com',
            name: 'Jane Doe',
            enterprise: 'Global Industries',
            role: 'manager',
            status: 'active',
            created_at: '2023-07-14T10:15:00Z'
          },
          {
            id: 3,
            email: 'mike.johnson@techsolutions.com',
            name: 'Mike Johnson',
            enterprise: 'Tech Solutions Inc',
            role: 'analyst',
            status: 'active',
            created_at: '2023-07-13T14:45:00Z'
          },
          {
            id: 4,
            email: 'sarah.williams@cityofmetropolis.gov',
            name: 'Sarah Williams',
            enterprise: 'City of Metropolis',
            role: 'manager',
            status: 'active',
            created_at: '2023-07-12T09:20:00Z'
          },
          {
            id: 5,
            email: 'robert.brown@stateuniversity.edu',
            name: 'Robert Brown',
            enterprise: 'State University',
            role: 'admin',
            status: 'pending',
            created_at: '2023-07-11T16:10:00Z'
          }
        ],
        recentDatasets: [
          {
            id: 1,
            name: 'Global Market Data',
            type: 'reference',
            status: 'active',
            size: '2.5 GB',
            last_updated: '2023-07-15T10:30:00Z'
          },
          {
            id: 2,
            name: 'Customer Transactions',
            type: 'transactional',
            status: 'active',
            size: '4.8 GB',
            last_updated: '2023-07-14T14:20:00Z'
          },
          {
            id: 3,
            name: 'Risk Analytics',
            type: 'analytical',
            status: 'active',
            size: '1.2 GB',
            last_updated: '2023-07-13T09:15:00Z'
          }
        ],
        recentReports: [
          {
            id: 1,
            name: 'System Performance Report',
            type: 'system',
            status: 'active',
            generated_at: '2023-07-15T10:30:00Z'
          },
          {
            id: 2,
            name: 'Enterprise Usage Summary',
            type: 'enterprise',
            status: 'active',
            generated_at: '2023-07-14T14:20:00Z'
          },
          {
            id: 3,
            name: 'User Activity Report',
            type: 'user',
            status: 'active',
            generated_at: '2023-07-13T09:15:00Z'
          }
        ],
        recentPresentations: [
          {
            id: 1,
            name: 'Enterprise Solution Overview',
            status: 'active',
            views: 245,
            last_updated: '2023-07-15T10:30:00Z'
          },
          {
            id: 2,
            name: 'Product Roadmap 2023',
            status: 'active',
            views: 187,
            last_updated: '2023-07-10T14:20:00Z'
          },
          {
            id: 3,
            name: 'Financial Services Solutions',
            status: 'active',
            views: 156,
            last_updated: '2023-07-05T09:15:00Z'
          }
        ]
      });
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError(err.message || 'Failed to load dashboard data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Fetch data on component mount
  useEffect(() => {
    fetchDashboardData();
  }, []);
  
  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Get activity icon
  const getActivityIcon = (type) => {
    switch (type) {
      case 'enterprise_created':
        return faBuilding;
      case 'user_verified':
        return faUserCheck;
      case 'agreement_updated':
        return faFileContract;
      case 'product_added':
        return faBox;
      case 'subscription_renewed':
        return faSync;
      default:
        return faBell;
    }
  };
  
  // Get activity text
  const getActivityText = (activity) => {
    switch (activity.type) {
      case 'enterprise_created':
        return `${activity.user} created enterprise "${activity.entity}"`;
      case 'user_verified':
        return `${activity.user} verified user "${activity.entity}"`;
      case 'agreement_updated':
        return `${activity.user} updated agreement for "${activity.entity}"`;
      case 'product_added':
        return `${activity.user} added product "${activity.entity}"`;
      case 'subscription_renewed':
        return `${activity.user} renewed subscription for "${activity.entity}"`;
      default:
        return `${activity.user} performed action on "${activity.entity}"`;
    }
  };
  
  // Get color for enterprise type
  const getColorForEnterpriseType = (type) => {
    switch (type) {
      case 'corporate': return 'var(--color-primary)';
      case 'government': return 'var(--color-success)';
      case 'educational': return 'var(--color-info)';
      case 'nonprofit': return 'var(--color-warning)';
      default: return 'var(--color-gray-400)';
    }
  };
  
  // Get color for status
  const getColorForStatus = (status) => {
    switch (status) {
      case 'active': return 'var(--color-success)';
      case 'pending': return 'var(--color-warning)';
      case 'suspended': return 'var(--color-error)';
      case 'inactive': return 'var(--color-gray-400)';
      case 'draft': return 'var(--color-info)';
      case 'archived': return 'var(--color-gray-600)';
      default: return 'var(--color-primary)';
    }
  };
  
  // Get color for product category
  const getColorForProductCategory = (category) => {
    switch (category) {
      case 'analytics': return 'var(--color-primary)';
      case 'risk_management': return 'var(--color-success)';
      case 'compliance': return 'var(--color-info)';
      case 'reporting': return 'var(--color-warning)';
      default: return 'var(--color-gray-400)';
    }
  };
  
  // Get color for subscription plan
  const getColorForSubscriptionPlan = (plan) => {
    switch (plan) {
      case 'enterprise': return 'var(--color-primary)';
      case 'professional': return 'var(--color-success)';
      case 'standard': return 'var(--color-info)';
      default: return 'var(--color-gray-400)';
    }
  };
  
  // Get color for user role
  const getColorForUserRole = (role) => {
    switch (role) {
      case 'admin': return 'var(--color-primary)';
      case 'manager': return 'var(--color-success)';
      case 'analyst': return 'var(--color-info)';
      case 'user': return 'var(--color-warning)';
      default: return 'var(--color-gray-400)';
    }
  };
  
  // Get color for dataset type
  const getColorForDatasetType = (type) => {
    switch (type) {
      case 'reference': return 'var(--color-primary)';
      case 'transactional': return 'var(--color-success)';
      case 'analytical': return 'var(--color-info)';
      default: return 'var(--color-gray-400)';
    }
  };
  
  // Get color for report type
  const getColorForReportType = (type) => {
    switch (type) {
      case 'system': return 'var(--color-primary)';
      case 'enterprise': return 'var(--color-success)';
      case 'user': return 'var(--color-info)';
      default: return 'var(--color-gray-400)';
    }
  };
  
  // Get color for log severity
  const getColorForLogSeverity = (severity) => {
    switch (severity) {
      case 'info': return 'var(--color-info)';
      case 'warning': return 'var(--color-warning)';
      case 'error': return 'var(--color-error)';
      default: return 'var(--color-gray-400)';
    }
  };
  
  // Loading state
  if (isLoading && !dashboardData) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner}></div>
        <p>{t('loading_dashboard_data')}</p>
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <div className={styles.error}>
        <FontAwesomeIcon icon={faExclamationCircle} className={styles.errorIcon} />
        <p>{error}</p>
        <button className={styles.retryButton} onClick={fetchDashboardData}>
          <FontAwesomeIcon icon={faSync} />
          <span>{t('retry')}</span>
        </button>
      </div>
    );
  }
  
  // Extract values from the data structure
  const totalEnterprises = dashboardData?.enterprise_metrics?.total_enterprises || 0;
  const activeEnterprises = dashboardData?.enterprise_metrics?.active_enterprises || 0;
  const totalUsers = dashboardData?.user_metrics?.total_users || 0;
  const pendingVerifications = dashboardData?.user_metrics?.pending_verifications || 0;
  const activeSubscriptions = dashboardData?.subscription_metrics?.active_subscriptions || 0;
  const expiringAgreements = dashboardData?.agreement_metrics?.expiring_agreements || 0;
  const recentCommunications = dashboardData?.communication_metrics?.total_communications || 0;
  const activeProducts = dashboardData?.product_metrics?.active_products || 0;
  const totalDatasets = dashboardData?.dataset_metrics?.total_datasets || 0;
  const totalReports = dashboardData?.report_metrics?.total_reports || 0;
  const totalPresentations = dashboardData?.pitch_access_metrics?.total_presentations || 0;
  
  return (
    <div className={styles.dashboard}>
      <div className={styles.dashboardHeader}>
        <h1 className={styles.title}>{t('super_admin_dashboard')}</h1>
        <div className={styles.dashboardInfo}>
          <div className={styles.reportDate}>
            <FontAwesomeIcon icon={faCalendarAlt} className={styles.reportDateIcon} />
            <span>{t('report_date')}: {formatDate(dashboardData?.report_date)}</span>
          </div>
          <button className={styles.refreshButton} onClick={fetchDashboardData}>
            <FontAwesomeIcon icon={faSync} />
            <span>{t('refresh')}</span>
          </button>
        </div>
      </div>
      
      <div className={styles.tabs}>
        <button 
          className={`${styles.tabButton} ${activeTab === 'overview' ? styles.active : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <FontAwesomeIcon icon={faTachometerAlt} />
          <span>{t('overview')}</span>
        </button>
        <button 
          className={`${styles.tabButton} ${activeTab === 'settings' ? styles.active : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          <FontAwesomeIcon icon={faCog} />
          <span>{t('settings')}</span>
        </button>
        <button 
          className={`${styles.tabButton} ${activeTab === 'pitch-access' ? styles.active : ''}`}
          onClick={() => setActiveTab('pitch-access')}
        >
          <FontAwesomeIcon icon={faChalkboardTeacher} />
          <span>{t('pitch_access')}</span>
        </button>
        <button 
          className={`${styles.tabButton} ${activeTab === 'enterprises' ? styles.active : ''}`}
          onClick={() => setActiveTab('enterprises')}
        >
          <FontAwesomeIcon icon={faBuilding} />
          <span>{t('enterprises')}</span>
        </button>
        <button 
          className={`${styles.tabButton} ${activeTab === 'users' ? styles.active : ''}`}
          onClick={() => setActiveTab('users')}
        >
          <FontAwesomeIcon icon={faUsers} />
          <span>{t('users')}</span>
        </button>
        <button 
          className={`${styles.tabButton} ${activeTab === 'products' ? styles.active : ''}`}
          onClick={() => setActiveTab('products')}
        >
          <FontAwesomeIcon icon={faBox} />
          <span>{t('products')}</span>
        </button>
        <button 
          className={`${styles.tabButton} ${activeTab === 'agreements' ? styles.active : ''}`}
          onClick={() => setActiveTab('agreements')}
        >
          <FontAwesomeIcon icon={faFileContract} />
          <span>{t('agreements')}</span>
        </button>
        <button 
          className={`${styles.tabButton} ${activeTab === 'subscriptions' ? styles.active : ''}`}
          onClick={() => setActiveTab('subscriptions')}
        >
          <FontAwesomeIcon icon={faCreditCard} />
          <span>{t('subscriptions')}</span>
        </button>
        <button 
          className={`${styles.tabButton} ${activeTab === 'rbac' ? styles.active : ''}`}
          onClick={() => setActiveTab('rbac')}
        >
          <FontAwesomeIcon icon={faShieldAlt} />
          <span>{t('rbac')}</span>
        </button>
        <button 
          className={`${styles.tabButton} ${activeTab === 'communications' ? styles.active : ''}`}
          onClick={() => setActiveTab('communications')}
        >
          <FontAwesomeIcon icon={faEnvelope} />
          <span>{t('communications')}</span>
        </button>
        <button 
          className={`${styles.tabButton} ${activeTab === 'datasets' ? styles.active : ''}`}
          onClick={() => setActiveTab('datasets')}
        >
          <FontAwesomeIcon icon={faDatabase} />
          <span>{t('datasets')}</span>
        </button>
        <button 
          className={`${styles.tabButton} ${activeTab === 'reports' ? styles.active : ''}`}
          onClick={() => setActiveTab('reports')}
        >
          <FontAwesomeIcon icon={faChartBar} />
          <span>{t('reports')}</span>
        </button>
        <button 
          className={`${styles.tabButton} ${activeTab === 'audit-logs' ? styles.active : ''}`}
          onClick={() => setActiveTab('audit-logs')}
        >
          <FontAwesomeIcon icon={faHistory} />
          <span>{t('audit_logs')}</span>
        </button>
      </div>
      
      {activeTab === 'overview' && (
        <div className={styles.overviewTab}>
          <div className={styles.statsGrid}>
            <SuperAdminCard
              title={t('total_enterprises')}
              value={totalEnterprises}
              subtitle={`${activeEnterprises} ${t('active')}`}
              icon={faBuilding}
              onClick={() => onNavigate('enterprises')}
              actionText={t('go_to_enterprise_management')}
              actionIcon={faArrowRight}
              color="primary"
            />
            <SuperAdminCard
              title={t('total_users')}
              value={totalUsers}
              subtitle={`${pendingVerifications} ${t('pending_verification')}`}
              icon={faUsers}
              onClick={() => onNavigate('users')}
              actionText={t('go_to_user_management')}
              actionIcon={faArrowRight}
              color="info"
            />
            <SuperAdminCard
              title={t('active_products')}
              value={activeProducts}
              subtitle={t('all_products_active')}
              icon={faBox}
              onClick={() => onNavigate('products')}
              actionText={t('go_to_product_management')}
              actionIcon={faArrowRight}
              color="success"
            />
            <SuperAdminCard
              title={t('active_subscriptions')}
              value={activeSubscriptions}
              subtitle={`${dashboardData?.subscription_metrics?.expiring_soon} ${t('expiring_soon')}`}
              icon={faCreditCard}
              onClick={() => onNavigate('subscriptions')}
              actionText={t('go_to_subscription_management')}
              actionIcon={faArrowRight}
              color="primary"
            />
            <SuperAdminCard
              title={t('agreements')}
              value={dashboardData?.agreement_metrics?.total_agreements}
              subtitle={`${expiringAgreements} ${t('expiring')}`}
              icon={faFileContract}
              status={expiringAgreements > 0 ? 'warning' : 'success'}
              onClick={() => onNavigate('agreements')}
              actionText={t('go_to_agreement_management')}
              actionIcon={faArrowRight}
              color="warning"
            />
            <SuperAdminCard
              title={t('communications')}
              value={recentCommunications}
              subtitle={t('recent_communications')}
              icon={faEnvelope}
              onClick={() => onNavigate('communications')}
              actionText={t('go_to_communication_management')}
              actionIcon={faArrowRight}
              color="info"
            />
            <SuperAdminCard
              title={t('presentations')}
              value={totalPresentations}
              subtitle={`${dashboardData?.pitch_access_metrics?.active_presentations} ${t('active')}`}
              icon={faChalkboardTeacher}
              onClick={() => onNavigate('pitch-access')}
              actionText={t('go_to_pitch_access')}
              actionIcon={faArrowRight}
              color="primary"
            />
            <SuperAdminCard
              title={t('datasets')}
              value={totalDatasets}
              subtitle={`${dashboardData?.dataset_metrics?.active_datasets} ${t('active')}`}
              icon={faDatabase}
              onClick={() => onNavigate('datasets')}
              actionText={t('go_to_dataset_management')}
              actionIcon={faArrowRight}
              color="success"
            />
          </div>
          
          <div className={styles.quickActions}>
            <h2 className={styles.sectionTitle}>{t('quick_actions')}</h2>
            <div className={styles.actionButtons}>
              <button 
                className={styles.actionButton}
                onClick={() => onNavigate('enterprises')}
              >
                <FontAwesomeIcon icon={faPlusCircle} className={styles.actionIcon} />
                <span>{t('create_enterprise')}</span>
              </button>
              <button 
                className={styles.actionButton}
                onClick={() => onNavigate('users')}
              >
                <FontAwesomeIcon icon={faUserPlus} className={styles.actionIcon} />
                <span>{t('add_user')}</span>
              </button>
              <button 
                className={styles.actionButton}
                onClick={() => onNavigate('products')}
              >
                <FontAwesomeIcon icon={faBoxOpen} className={styles.actionIcon} />
                <span>{t('add_product')}</span>
              </button>
              <button 
                className={styles.actionButton}
                onClick={() => onNavigate('agreements')}
              >
                <FontAwesomeIcon icon={faFileAlt} className={styles.actionIcon} />
                <span>{t('create_agreement')}</span>
              </button>
            </div>
          </div>
          
          <div className={styles.summarySection}>
            <h2 className={styles.sectionTitle}>{t('system_summary')}</h2>
            <div className={styles.summaryGrid}>
              <div className={styles.summaryCard}>
                <div className={styles.summaryHeader}>
                  <h3 className={styles.summaryTitle}>{t('enterprises_by_type')}</h3>
                  <FontAwesomeIcon icon={faBuilding} className={styles.summaryIcon} />
                </div>
                <div className={styles.summaryContent}>
                  {Object.entries(dashboardData?.enterprise_metrics?.enterprises_by_type || {}).map(([type, data]) => (
                    <div className={styles.summaryItem} key={type}>
                      <div className={styles.summaryItemLabel}>{t(type)}</div>
                      <div className={styles.summaryItemValue}>{data.count}</div>
                      <div className={styles.summaryItemBar}>
                        <div 
                          className={styles.summaryItemProgress} 
                          style={{ 
                            width: `${(data.count / totalEnterprises) * 100}%`,
                            backgroundColor: getColorForEnterpriseType(type)
                          }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className={styles.summaryCard}>
                <div className={styles.summaryHeader}>
                  <h3 className={styles.summaryTitle}>{t('users_by_role')}</h3>
                  <FontAwesomeIcon icon={faUsers} className={styles.summaryIcon} />
                </div>
                <div className={styles.summaryContent}>
                  {Object.entries(dashboardData?.user_metrics?.users_by_role || {}).map(([role, data]) => (
                    <div className={styles.summaryItem} key={role}>
                      <div className={styles.summaryItemLabel}>{t(role)}</div>
                      <div className={styles.summaryItemValue}>{data.count}</div>
                      <div className={styles.summaryItemBar}>
                        <div 
                          className={styles.summaryItemProgress} 
                          style={{ 
                            width: `${(data.count / totalUsers) * 100}%`,
                            backgroundColor: getColorForUserRole(role)
                          }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className={styles.summaryCard}>
                <div className={styles.summaryHeader}>
                  <h3 className={styles.summaryTitle}>{t('products_by_category')}</h3>
                  <FontAwesomeIcon icon={faBox} className={styles.summaryIcon} />
                </div>
                <div className={styles.summaryContent}>
                  {Object.entries(dashboardData?.product_metrics?.products_by_category || {}).map(([category, data]) => (
                    <div className={styles.summaryItem} key={category}>
                      <div className={styles.summaryItemLabel}>{t(category)}</div>
                      <div className={styles.summaryItemValue}>{data.count}</div>
                      <div className={styles.summaryItemBar}>
                        <div 
                          className={styles.summaryItemProgress} 
                          style={{ 
                            width: `${(data.count / activeProducts) * 100}%`,
                            backgroundColor: getColorForProductCategory(category)
                          }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          
          <div className={styles.tablesGrid}>
            <div className={styles.tableCard}>
              <div className={styles.tableHeader}>
                <h3 className={styles.tableTitle}>{t('recent_enterprises')}</h3>
                <button 
                  className={styles.viewAllButton}
                  onClick={() => onNavigate('enterprises')}
                >
                  {t('view_all')}
                </button>
              </div>
              <div className={styles.tableContainer}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>{t('enterprise_name')}</th>
                      <th>{t('type')}</th>
                      <th>{t('users')}</th>
                      <th>{t('subscription')}</th>
                      <th>{t('created_date')}</th>
                      <th>{t('status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData?.recentEnterprises?.map(enterprise => (
                      <tr 
                        key={enterprise.id}
                        onClick={() => onNavigate('enterprises')}
                        className={styles.clickableRow}
                      >
                        <td>{enterprise.name}</td>
                        <td>{t(enterprise.type)}</td>
                        <td>{enterprise.users}</td>
                        <td>{t(enterprise.subscription_plan)}</td>
                        <td>{formatDate(enterprise.created_at)}</td>
                        <td>
                          <span className={`${styles.statusBadge} ${styles[enterprise.status]}`}>
                            {t(enterprise.status)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            
            <div className={styles.tableCard}>
              <div className={styles.tableHeader}>
                <h3 className={styles.tableTitle}>{t('recent_users')}</h3>
                <button 
                  className={styles.viewAllButton}
                  onClick={() => onNavigate('users')}
                >
                  {t('view_all')}
                </button>
              </div>
              <div className={styles.tableContainer}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>{t('name')}</th>
                      <th>{t('email')}</th>
                      <th>{t('enterprise')}</th>
                      <th>{t('role')}</th>
                      <th>{t('created_date')}</th>
                      <th>{t('status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData?.recentUsers?.map(user => (
                      <tr 
                        key={user.id}
                        onClick={() => onNavigate('users')}
                        className={styles.clickableRow}
                      >
                        <td>{user.name}</td>
                        <td>{user.email}</td>
                        <td>{user.enterprise}</td>
                        <td>{t(user.role)}</td>
                        <td>{formatDate(user.created_at)}</td>
                        <td>
                          <span className={`${styles.statusBadge} ${styles[user.status]}`}>
                            {t(user.status)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          
          <div className={styles.activitySection}>
            <h2 className={styles.sectionTitle}>{t('recent_activity')}</h2>
            <div className={styles.activityList}>
              {dashboardData?.recentActivities?.map((activity) => (
                <div key={activity.id} className={styles.activityItem}>
                  <div className={styles.activityIcon}>
                    <FontAwesomeIcon icon={getActivityIcon(activity.type)} />
                  </div>
                  <div className={styles.activityContent}>
                    <div className={styles.activityText}>
                      {getActivityText(activity)}
                    </div>
                    <div className={styles.activityTime}>
                      {formatDate(activity.timestamp)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className={styles.viewAllContainer}>
              <button 
                className={styles.viewAllButton}
                onClick={() => onNavigate('audit-logs')}
              >
                {t('view_all_activity')}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {activeTab === 'enterprises' && (
        <div className={styles.enterprisesTab}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>{t('enterprise_management')}</h2>
            <button 
              className={styles.actionButton}
              onClick={() => onNavigate('enterprises')}
            >
              <span>{t('manage_enterprises')}</span>
              <FontAwesomeIcon icon={faArrowRight} />
            </button>
          </div>
          
          <div className={styles.enterprisesOverview}>
            <div className={styles.overviewCard}>
              <div className={styles.overviewHeader}>
                <h3 className={styles.overviewTitle}>{t('enterprises_by_status')}</h3>
                <FontAwesomeIcon icon={faBuilding} className={styles.overviewIcon} />
              </div>
              <div className={styles.overviewContent}>
                <DonutChart 
                  data={dashboardData?.enterprise_metrics?.enterprises_by_status || {}}
                  colorFn={getColorForStatus}
                  centerValue={totalEnterprises}
                  centerLabel={t('enterprises')}
                />
              </div>
            </div>
            
            <div className={styles.overviewCard}>
              <div className={styles.overviewHeader}>
                <h3 className={styles.overviewTitle}>{t('enterprises_by_type')}</h3>
                <FontAwesomeIcon icon={faBuilding} className={styles.overviewIcon} />
              </div>
              <div className={styles.overviewContent}>
                <BarChart 
                  data={dashboardData?.enterprise_metrics?.enterprises_by_type || {}}
                  colorFn={getColorForEnterpriseType}
                  total={totalEnterprises}
                />
              </div>
            </div>
          </div>
          
          <div className={styles.enterprisesList}>
            <h3 className={styles.listTitle}>{t('enterprise_list')}</h3>
            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>{t('enterprise_name')}</th>
                    <th>{t('type')}</th>
                    <th>{t('users')}</th>
                    <th>{t('subscription')}</th>
                    <th>{t('created_date')}</th>
                    <th>{t('status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboardData?.recentEnterprises?.map(enterprise => (
                    <tr 
                      key={enterprise.id}
                      onClick={() => onNavigate('enterprises')}
                      className={styles.clickableRow}
                    >
                      <td>{enterprise.name}</td>
                      <td>{t(enterprise.type)}</td>
                      <td>{enterprise.users}</td>
                      <td>{t(enterprise.subscription_plan)}</td>
                      <td>{formatDate(enterprise.created_at)}</td>
                      <td>
                        <span className={`${styles.statusBadge} ${styles[enterprise.status]}`}>
                          {t(enterprise.status)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      
      {activeTab === 'users' && (
        <div className={styles.usersTab}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>{t('user_management')}</h2>
            <button 
              className={styles.actionButton}
              onClick={() => onNavigate('users')}
            >
              <span>{t('manage_users')}</span>
              <FontAwesomeIcon icon={faArrowRight} />
            </button>
          </div>
          
          <div className={styles.usersOverview}>
            <div className={styles.overviewCard}>
              <div className={styles.overviewHeader}>
                <h3 className={styles.overviewTitle}>{t('users_by_role')}</h3>
                <FontAwesomeIcon icon={faUsers} className={styles.overviewIcon} />
              </div>
              <div className={styles.overviewContent}>
                <DonutChart 
                  data={dashboardData?.user_metrics?.users_by_role || {}}
                  colorFn={getColorForUserRole}
                  centerValue={totalUsers}
                  centerLabel={t('users')}
                />
              </div>
            </div>
            
            <div className={styles.metricsCards}>
              <div className={styles.metricCard}>
                <div className={styles.metricHeader}>
                  <h3 className={styles.metricTitle}>{t('total_users')}</h3>
                  <FontAwesomeIcon icon={faUsers} className={styles.metricIcon} />
                </div>
                <p className={styles.metricValue}>{totalUsers}</p>
                <p className={styles.metricSubtitle}>{t('registered_users')}</p>
              </div>
              
              <div className={styles.metricCard}>
                <div className={styles.metricHeader}>
                  <h3 className={styles.metricTitle}>{t('active_users')}</h3>
                  <FontAwesomeIcon icon={faUserCheck} className={styles.metricIcon} />
                </div>
                <p className={styles.metricValue}>{dashboardData?.user_metrics?.active_users}</p>
                <p className={styles.metricSubtitle}>{t('currently_active')}</p>
              </div>
              
              <div className={styles.metricCard}>
                <div className={styles.metricHeader}>
                  <h3 className={styles.metricTitle}>{t('pending_verifications')}</h3>
                  <FontAwesomeIcon icon={faUserClock} className={styles.metricIcon} />
                </div>
                <p className={styles.metricValue}>{pendingVerifications}</p>
                <p className={styles.metricSubtitle}>{t('awaiting_verification')}</p>
              </div>
            </div>
          </div>
          
          <div className={styles.usersList}>
            <h3 className={styles.listTitle}>{t('recent_users')}</h3>
            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>{t('name')}</th>
                    <th>{t('email')}</th>
                    <th>{t('enterprise')}</th>
                    <th>{t('role')}</th>
                    <th>{t('created_date')}</th>
                    <th>{t('status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboardData?.recentUsers?.map(user => (
                    <tr 
                      key={user.id}
                      onClick={() => onNavigate('users')}
                      className={styles.clickableRow}
                    >
                      <td>{user.name}</td>
                      <td>{user.email}</td>
                      <td>{user.enterprise}</td>
                      <td>{t(user.role)}</td>
                      <td>{formatDate(user.created_at)}</td>
                      <td>
                        <span className={`${styles.statusBadge} ${styles[user.status]}`}>
                          {t(user.status)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      
      {activeTab === 'products' && (
        <div className={styles.productsTab}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>{t('product_management')}</h2>
            <button 
              className={styles.actionButton}
              onClick={() => onNavigate('products')}
            >
              <span>{t('manage_products')}</span>
              <FontAwesomeIcon icon={faArrowRight} />
            </button>
          </div>
          
          <div className={styles.productsOverview}>
            <div className={styles.overviewCard}>
              <div className={styles.overviewHeader}>
                <h3 className={styles.overviewTitle}>{t('products_by_category')}</h3>
                <FontAwesomeIcon icon={faBox} className={styles.overviewIcon} />
              </div>
              <div className={styles.overviewContent}>
                <DonutChart 
                  data={dashboardData?.product_metrics?.products_by_category || {}}
                  colorFn={getColorForProductCategory}
                  centerValue={activeProducts}
                  centerLabel={t('products')}
                />
              </div>
            </div>
            
            <div className={styles.productsList}>
              <h3 className={styles.listTitle}>{t('product_list')}</h3>
              <div className={styles.productCards}>
                {Object.entries(dashboardData?.product_metrics?.products_by_category || {}).map(([category, data]) => (
                  <div 
                    key={category}
                    className={styles.productCard}
                    onClick={() => onNavigate('products')}
                  >
                    <div 
                      className={styles.productIcon}
                      style={{ backgroundColor: getColorForProductCategory(category) }}
                    >
                      <FontAwesomeIcon icon={
                        category === 'analytics' ? faChartBar : 
                        category === 'risk_management' ? faShieldAlt : 
                        category === 'compliance' ? faFileContract : 
                        faDatabase
                      } />
                    </div>
                    <div className={styles.productInfo}>
                      <h4 className={styles.productCategory}>{t(category)}</h4>
                      <p className={styles.productCount}>{data.count} {t('products')}</p>
                      <button className={styles.productViewButton}>
                        {t('view_details')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {activeTab === 'subscriptions' && (
        <div className={styles.subscriptionsTab}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>{t('subscription_management')}</h2>
            <button 
              className={styles.actionButton}
              onClick={() => onNavigate('subscriptions')}
            >
              <span>{t('manage_subscriptions')}</span>
              <FontAwesomeIcon icon={faArrowRight} />
            </button>
          </div>
          
          <div className={styles.subscriptionsOverview}>
            <div className={styles.overviewCard}>
              <div className={styles.overviewHeader}>
                <h3 className={styles.overviewTitle}>{t('subscriptions_by_plan')}</h3>
                <FontAwesomeIcon icon={faCreditCard} className={styles.overviewIcon} />
              </div>
              <div className={styles.overviewContent}>
                <DonutChart 
                  data={dashboardData?.subscription_metrics?.subscriptions_by_plan || {}}
                  colorFn={getColorForSubscriptionPlan}
                  centerValue={activeSubscriptions}
                  centerLabel={t('subscriptions')}
                />
              </div>
            </div>
            
            <div className={styles.metricsCards}>
              <div className={styles.metricCard}>
                <div className={styles.metricHeader}>
                  <h3 className={styles.metricTitle}>{t('active_subscriptions')}</h3>
                  <FontAwesomeIcon icon={faCheckCircle} className={styles.metricIcon} />
                </div>
                <p className={styles.metricValue}>{activeSubscriptions}</p>
                <p className={styles.metricSubtitle}>{t('current_subscriptions')}</p>
              </div>
              
              <div className={styles.metricCard}>
                <div className={styles.metricHeader}>
                  <h3 className={styles.metricTitle}>{t('expiring_soon')}</h3>
                  <FontAwesomeIcon icon={faExclamationTriangle} className={styles.metricIcon} />
                </div>
                <p className={styles.metricValue}>{dashboardData?.subscription_metrics?.expiring_soon}</p>
                <p className={styles.metricSubtitle}>{t('within_30_days')}</p>
              </div>
            </div>
          </div>
          
          <div className={styles.subscriptionsList}>
            <h3 className={styles.listTitle}>{t('enterprise_subscriptions')}</h3>
            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>{t('enterprise')}</th>
                    <th>{t('plan')}</th>
                    <th>{t('users')}</th>
                    <th>{t('start_date')}</th>
                    <th>{t('end_date')}</th>
                    <th>{t('status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboardData?.recentEnterprises?.map(enterprise => (
                    <tr 
                      key={enterprise.id}
                      onClick={() => onNavigate('subscriptions')}
                      className={styles.clickableRow}
                    >
                      <td>{enterprise.name}</td>
                      <td>{t(enterprise.subscription_plan)}</td>
                      <td>{enterprise.users}</td>
                      <td>{formatDate(enterprise.created_at)}</td>
                      <td>{formatDate(new Date(new Date(enterprise.created_at).setFullYear(new Date(enterprise.created_at).getFullYear() + 1)))}</td>
                      <td>
                        <span className={`${styles.statusBadge} ${styles.active}`}>
                          {t('active')}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      
      {activeTab === 'agreements' && (
        <div className={styles.agreementsTab}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>{t('agreement_management')}</h2>
            <button 
              className={styles.actionButton}
              onClick={() => onNavigate('agreements')}
            >
              <span>{t('manage_agreements')}</span>
              <FontAwesomeIcon icon={faArrowRight} />
            </button>
          </div>
          
          <div className={styles.agreementsOverview}>
            <div className={styles.overviewCard}>
              <div className={styles.overviewHeader}>
                <h3 className={styles.overviewTitle}>{t('agreements_by_type')}</h3>
                <FontAwesomeIcon icon={faFileContract} className={styles.overviewIcon} />
              </div>
              <div className={styles.overviewContent}>
                <DonutChart 
                  data={dashboardData?.agreement_metrics?.agreements_by_type || {}}
                  colorFn={(type) => {
                    switch (type) {
                      case 'service': return 'var(--color-primary)';
                      case 'license': return 'var(--color-success)';
                      case 'partnership': return 'var(--color-info)';
                      default: return 'var(--color-gray-400)';
                    }
                  }}
                  centerValue={dashboardData?.agreement_metrics?.total_agreements}
                  centerLabel={t('agreements')}
                />
              </div>
            </div>
            
            <div className={styles.metricsCards}>
              <div className={styles.metricCard}>
                <div className={styles.metricHeader}>
                  <h3 className={styles.metricTitle}>{t('active_agreements')}</h3>
                  <FontAwesomeIcon icon={faCheckCircle} className={styles.metricIcon} />
                </div>
                <p className={styles.metricValue}>{dashboardData?.agreement_metrics?.active_agreements}</p>
                <p className={styles.metricSubtitle}>{t('current_agreements')}</p>
              </div>
              
              <div className={styles.metricCard}>
                <div className={styles.metricHeader}>
                  <h3 className={styles.metricTitle}>{t('expiring_agreements')}</h3>
                  <FontAwesomeIcon icon={faExclamationTriangle} className={styles.metricIcon} />
                </div>
                <p className={styles.metricValue}>{expiringAgreements}</p>
                <p className={styles.metricSubtitle}>{t('require_attention')}</p>
              </div>
            </div>
          </div>
          
          <div className={styles.agreementsList}>
            <h3 className={styles.listTitle}>{t('recent_agreements')}</h3>
            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>{t('enterprise')}</th>
                    <th>{t('agreement_type')}</th>
                    <th>{t('start_date')}</th>
                    <th>{t('end_date')}</th>
                    <th>{t('status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboardData?.recentEnterprises?.slice(0, 5).map((enterprise, index) => (
                    <tr 
                      key={enterprise.id}
                      onClick={() => onNavigate('agreements')}
                      className={styles.clickableRow}
                    >
                      <td>{enterprise.name}</td>
                      <td>{t(Object.keys(dashboardData?.agreement_metrics?.agreements_by_type || {})[index % 3])}</td>
                      <td>{formatDate(enterprise.created_at)}</td>
                      <td>{formatDate(new Date(new Date(enterprise.created_at).setFullYear(new Date(enterprise.created_at).getFullYear() + 2)))}</td>
                      <td>
                        <span className={`${styles.statusBadge} ${index === 0 ? styles.warning : styles.active}`}>
                          {index === 0 ? t('expiring_soon') : t('active')}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      
      {activeTab === 'rbac' && (
        <div className={styles.rbacTab}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>{t('rbac_management')}</h2>
            <button 
              className={styles.actionButton}
              onClick={() => onNavigate('rbac')}
            >
              <span>{t('manage_rbac')}</span>
              <FontAwesomeIcon icon={faArrowRight} />
            </button>
          </div>
          
          <div className={styles.rbacOverview}>
            <div className={styles.metricsCards}>
              <div className={styles.metricCard}>
                <div className={styles.metricHeader}>
                  <h3 className={styles.metricTitle}>{t('total_roles')}</h3>
                  <FontAwesomeIcon icon={faShieldAlt} className={styles.metricIcon} />
                </div>
                <p className={styles.metricValue}>{dashboardData?.rbac_metrics?.total_roles}</p>
                <p className={styles.metricSubtitle}>{t('defined_roles')}</p>
              </div>
              
              <div className={styles.metricCard}>
                <div className={styles.metricHeader}>
                  <h3 className={styles.metricTitle}>{t('total_permissions')}</h3>
                  <FontAwesomeIcon icon={faKey} className={styles.metricIcon} />
                </div>
                <p className={styles.metricValue}>{dashboardData?.rbac_metrics?.total_permissions}</p>
                <p className={styles.metricSubtitle}>{t('available_permissions')}</p>
              </div>
              
              <div className={styles.metricCard}>
                <div className={styles.metricHeader}>
                  <h3 className={styles.metricTitle}>{t('custom_roles')}</h3>
                  <FontAwesomeIcon icon={faUserCheck} className={styles.metricIcon} />
                </div>
                <p className={styles.metricValue}>{dashboardData?.rbac_metrics?.custom_roles}</p>
                <p className={styles.metricSubtitle}>{t('user_defined_roles')}</p>
              </div>
            </div>
            
            <div className={styles.placeholderContent}>
              <p>{t('rbac_dashboard_coming_soon')}</p>
              <button 
                className={styles.actionButton}
                onClick={() => onNavigate('rbac')}
              >
                <span>{t('go_to_rbac_management')}</span>
                <FontAwesomeIcon icon={faArrowRight} />
              </button>
            </div>
          </div>
        </div>
      )}
      
      {activeTab === 'communications' && (
        <div className={styles.communicationsTab}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>{t('communication_management')}</h2>
            <button 
              className={styles.actionButton}
              onClick={() => onNavigate('communications')}
            >
              <span>{t('manage_communications')}</span>
              <FontAwesomeIcon icon={faArrowRight} />
            </button>
          </div>
          
          <div className={styles.communicationsOverview}>
            <div className={styles.overviewCard}>
              <div className={styles.overviewHeader}>
                <h3 className={styles.overviewTitle}>{t('communications_by_type')}</h3>
                <FontAwesomeIcon icon={faEnvelope} className={styles.overviewIcon} />
              </div>
              <div className={styles.overviewContent}>
                <DonutChart 
                  data={dashboardData?.communication_metrics?.communications_by_type || {}}
                  colorFn={(type) => {
                    switch (type) {
                      case 'email': return 'var(--color-primary)';
                      case 'notification': return 'var(--color-success)';
                      case 'announcement': return 'var(--color-warning)';
                      default: return 'var(--color-gray-400)';
                    }
                  }}
                  centerValue={recentCommunications}
                  centerLabel={t('communications')}
                />
              </div>
            </div>
            
            <div className={styles.placeholderContent}>
              <p>{t('communications_dashboard_coming_soon')}</p>
              <button 
                className={styles.actionButton}
                onClick={() => onNavigate('communications')}
              >
                <span>{t('go_to_communication_management')}</span>
                <FontAwesomeIcon icon={faArrowRight} />
              </button>
            </div>
          </div>
        </div>
      )}
      
      {activeTab === 'datasets' && (
        <div className={styles.datasetsTab}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>{t('dataset_management')}</h2>
            <button 
              className={styles.actionButton}
              onClick={() => onNavigate('datasets')}
            >
              <span>{t('manage_datasets')}</span>
              <FontAwesomeIcon icon={faArrowRight} />
            </button>
          </div>
          
          <div className={styles.datasetsOverview}>
            <div className={styles.overviewCard}>
              <div className={styles.overviewHeader}>
                <h3 className={styles.overviewTitle}>{t('datasets_by_type')}</h3>
                <FontAwesomeIcon icon={faDatabase} className={styles.overviewIcon} />
              </div>
              <div className={styles.overviewContent}>
                <DonutChart 
                  data={dashboardData?.dataset_metrics?.datasets_by_type || {}}
                  colorFn={getColorForDatasetType}
                  centerValue={totalDatasets}
                  centerLabel={t('datasets')}
                />
              </div>
            </div>
            
            <div className={styles.metricsCards}>
              <div className={styles.metricCard}>
                <div className={styles.metricHeader}>
                  <h3 className={styles.metricTitle}>{t('total_datasets')}</h3>
                  <FontAwesomeIcon icon={faDatabase} className={styles.metricIcon} />
                </div>
                <p className={styles.metricValue}>{totalDatasets}</p>
                <p className={styles.metricSubtitle}>{t('available_datasets')}</p>
              </div>
              
              <div className={styles.metricCard}>
                <div className={styles.metricHeader}>
                  <h3 className={styles.metricTitle}>{t('active_datasets')}</h3>
                  <FontAwesomeIcon icon={faCheckCircle} className={styles.metricIcon} />
                </div>
                <p className={styles.metricValue}>{dashboardData?.dataset_metrics?.active_datasets}</p>
                <p className={styles.metricSubtitle}>{t('in_use_datasets')}</p>
              </div>
            </div>
          </div>
          
          <div className={styles.datasetsList}>
            <h3 className={styles.listTitle}>{t('recent_datasets')}</h3>
            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>{t('dataset_name')}</th>
                    <th>{t('type')}</th>
                    <th>{t('size')}</th>
                    <th>{t('last_updated')}</th>
                    <th>{t('status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboardData?.recentDatasets?.map(dataset => (
                    <tr 
                      key={dataset.id}
                      onClick={() => onNavigate('datasets')}
                      className={styles.clickableRow}
                    >
                      <td>{dataset.name}</td>
                      <td>{t(dataset.type)}</td>
                      <td>{dataset.size}</td>
                      <td>{formatDate(dataset.last_updated)}</td>
                      <td>
                        <span className={`${styles.statusBadge} ${styles[dataset.status]}`}>
                          {t(dataset.status)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      
      {activeTab === 'reports' && (
        <div className={styles.reportsTab}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>{t('report_management')}</h2>
            <button 
              className={styles.actionButton}
              onClick={() => onNavigate('reports')}
            >
              <span>{t('manage_reports')}</span>
              <FontAwesomeIcon icon={faArrowRight} />
            </button>
          </div>
          
          <div className={styles.reportsOverview}>
            <div className={styles.overviewCard}>
              <div className={styles.overviewHeader}>
                <h3 className={styles.overviewTitle}>{t('reports_by_type')}</h3>
                <FontAwesomeIcon icon={faChartBar} className={styles.overviewIcon} />
              </div>
              <div className={styles.overviewContent}>
                <DonutChart 
                  data={dashboardData?.report_metrics?.reports_by_type || {}}
                  colorFn={getColorForReportType}
                  centerValue={totalReports}
                  centerLabel={t('reports')}
                />
              </div>
            </div>
            
            <div className={styles.reportsList}>
              <h3 className={styles.listTitle}>{t('recent_reports')}</h3>
              <div className={styles.tableContainer}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>{t('report_name')}</th>
                      <th>{t('type')}</th>
                      <th>{t('generated_date')}</th>
                      <th>{t('status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData?.recentReports?.map(report => (
                      <tr 
                        key={report.id}
                        onClick={() => onNavigate('reports')}
                        className={styles.clickableRow}
                      >
                        <td>{report.name}</td>
                        <td>{t(report.type)}</td>
                        <td>{formatDate(report.generated_at)}</td>
                        <td>
                          <span className={`${styles.statusBadge} ${styles[report.status]}`}>
                            {t(report.status)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {activeTab === 'audit-logs' && (
        <div className={styles.auditLogsTab}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>{t('audit_logs')}</h2>
            <button 
              className={styles.actionButton}
              onClick={() => onNavigate('audit-logs')}
            >
              <span>{t('view_all_logs')}</span>
              <FontAwesomeIcon icon={faArrowRight} />
            </button>
          </div>
          
          <div className={styles.auditLogsOverview}>
            <div className={styles.overviewCard}>
              <div className={styles.overviewHeader}>
                <h3 className={styles.overviewTitle}>{t('logs_by_severity')}</h3>
                <FontAwesomeIcon icon={faHistory} className={styles.overviewIcon} />
              </div>
              <div className={styles.overviewContent}>
                <DonutChart 
                  data={dashboardData?.audit_metrics?.logs_by_severity || {}}
                  colorFn={getColorForLogSeverity}
                  centerValue={dashboardData?.audit_metrics?.total_logs}
                  centerLabel={t('logs')}
                />
              </div>
            </div>
            
            <div className={styles.activitySection}>
              <h3 className={styles.listTitle}>{t('recent_activity')}</h3>
              <div className={styles.activityList}>
                {dashboardData?.recentActivities?.map((activity) => (
                  <div key={activity.id} className={styles.activityItem}>
                    <div className={styles.activityIcon}>
                      <FontAwesomeIcon icon={getActivityIcon(activity.type)} />
                    </div>
                    <div className={styles.activityContent}>
                      <div className={styles.activityText}>
                        {getActivityText(activity)}
                      </div>
                      <div className={styles.activityTime}>
                        {formatDate(activity.timestamp)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {activeTab === 'settings' && (
        <div className={styles.settingsTab}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>{t('system_settings')}</h2>
            <button 
              className={styles.actionButton}
              onClick={() => onNavigate('settings')}
            >
              <span>{t('manage_settings')}</span>
              <FontAwesomeIcon icon={faArrowRight} />
            </button>
          </div>
          
          <div className={styles.settingsOverview}>
            <div className={styles.metricsCards}>
              <div className={styles.metricCard}>
                <div className={styles.metricHeader}>
                  <h3 className={styles.metricTitle}>{t('system_settings')}</h3>
                  <FontAwesomeIcon icon={faCog} className={styles.metricIcon} />
                </div>
                <p className={styles.metricValue}>{dashboardData?.settings_metrics?.system_settings}</p>
                <p className={styles.metricSubtitle}>{t('configurable_settings')}</p>
              </div>
              
              <div className={styles.metricCard}>
                <div className={styles.metricHeader}>
                  <h3 className={styles.metricTitle}>{t('custom_settings')}</h3>
                  <FontAwesomeIcon icon={faProjectDiagram} className={styles.metricIcon} />
                </div>
                <p className={styles.metricValue}>{dashboardData?.settings_metrics?.custom_settings}</p>
                <p className={styles.metricSubtitle}>{t('user_defined_settings')}</p>
              </div>
            </div>
            
            <div className={styles.placeholderContent}>
              <p>{t('settings_dashboard_coming_soon')}</p>
              <button 
                className={styles.actionButton}
                onClick={() => onNavigate('settings')}
              >
                <span>{t('go_to_system_settings')}</span>
                <FontAwesomeIcon icon={faArrowRight} />
              </button>
            </div>
          </div>
        </div>
      )}
      
      {activeTab === 'pitch-access' && (
        <div className={styles.pitchAccessTab}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>{t('pitch_access_management')}</h2>
            <button 
              className={styles.actionButton}
              onClick={() => onNavigate('pitch-access')}
            >
              <span>{t('manage_presentations')}</span>
              <FontAwesomeIcon icon={faArrowRight} />
            </button>
          </div>
          
          <div className={styles.pitchAccessOverview}>
            <div className={styles.overviewCard}>
              <div className={styles.overviewHeader}>
                <h3 className={styles.overviewTitle}>{t('presentations_by_status')}</h3>
                <FontAwesomeIcon icon={faChalkboardTeacher} className={styles.overviewIcon} />
              </div>
              <div className={styles.overviewContent}>
                <DonutChart 
                  data={dashboardData?.pitch_access_metrics?.presentations_by_status || {}}
                  colorFn={getColorForStatus}
                  centerValue={totalPresentations}
                  centerLabel={t('presentations')}
                />
              </div>
            </div>
            
            <div className={styles.presentationsList}>
              <h3 className={styles.listTitle}>{t('recent_presentations')}</h3>
              <div className={styles.tableContainer}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>{t('presentation_name')}</th>
                      <th>{t('views')}</th>
                      <th>{t('last_updated')}</th>
                      <th>{t('status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData?.recentPresentations?.map(presentation => (
                      <tr 
                        key={presentation.id}
                        onClick={() => onNavigate('pitch-access')}
                        className={styles.clickableRow}
                      >
                        <td>{presentation.name}</td>
                        <td>{presentation.views}</td>
                        <td>{formatDate(presentation.last_updated)}</td>
                        <td>
                          <span className={`${styles.statusBadge} ${styles[presentation.status]}`}>
                            {t(presentation.status)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Help button at the bottom */}
      <div className={styles.helpContainer}>
        <button className={styles.helpButton}>
          <FontAwesomeIcon icon={faQuestionCircle} />
          <span>{t('help_and_resources')}</span>
        </button>
      </div>
    </div>
  );
};

export default Dashboard;